import React, { useEffect, useRef, useState } from 'react';
import CloseCircleIcon from '../icons/CloseCircle';
import CloseCircleFillIcon from '../icons/CloseCircleFill';
import SlidersIcon from '../icons/Sliders';
import Button from './Button';
import FocusTrap from 'focus-trap-react';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import { getFacetForId } from '../utils';
import { LAYOUT_FILTER_FIELD } from '../utils/uiFilters';

const filtersButton = css`
  @media (min-width: 800px) {
    width: auto;
    margin-top: 20px;
  }

  svg {
    margin-right: 15px;
  }
`;
const filtersDialogHeader = css`
  align-items: center;
  border-bottom: 1px solid #e5dbd1;
  display: flex;
  justify-content: space-between;
  padding: 20px;

  @media (min-width: 800px) {
    padding: 20px 50px;
  }
`;
const filtersDialogHeaderTitle = css`
  font-size: 28px;
  font-size: 28px;
  margin: 0;
`;
const filtersDialogHeaderCloseButton = css`
  border: none;
  background: none;
  padding: 0;

  svg {
    fill: #1a1a1a;
    width: 40px;
    height: 40px;
  }

  &:focus {
    box-shadow: 0 0 0 3px #ffdd00;
    border-radius: 50%;
  }
`;
const filtersDialogContent = css`
  padding: 0 20px 0 20px;

  @media (min-width: 800px) {
    padding: 0 50px;
  }

  div:first-child {
    border-top: none;
  }
`;

const filtersDialogFooter = css`
  align-items: center;
  background-color: #e6e6e6;
  border-top: 1px solid #e5dbd1;
  display: flex;
  flex-direction: column-reverse;
  gap: 20px;
  padding: 20px;

  @media (min-width: 800px) {
    padding: 20px 50px;
    flex-direction: row;
    justify-content: space-between;
  }

  a {
    color: #1a1a1a;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 3px;

    &:hover {
      color: #1a1a1a;
      text-decoration-thickness: 2px;
      text-underline-offset: 2px;
    }
  }
`;

const filtersShowResultsButton = css`
  font-size: 18px;
  line-height: 25px;
  padding: 10px 15px;
  height: 50px;
  width: 100%;

  @media (min-width: 800px) {
    padding: 12px 34px;
    width: auto;
  }
`;

const FiltersDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: auto;
  background: #fff;
  box-shadow: 0 -2px 4px rgba(21, 21, 21, 0.15);
  transform: ${props => (props.isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  display: ${props => (props.isOpen ? 'block' : 'none')};
  max-height: 100dvh;
  max-width: none;
  width: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: none;
  z-index: 1000;

  @media (min-width: 800px) {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    max-width: 510px;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 21, 21, 0.6);
  z-index: 999;
`;

export default function FiltersButton({ SearchFilters, clearFilters, facets }) {
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isCloseFocused, setIsCloseFocused] = useState(false);
  const [filtersIsOpen, setFiltersIsOpen] = useState(false);
  const focusCaptureElementRef = useRef(null);
  const openFiltersButtonRef = useRef(null);

  // @todo Improve this duplicated functionality
  function getDocumentCount() {
    const allDocumentsFacet = getFacetForId(facets, 'count_document');

    if (
      allDocumentsFacet &&
      allDocumentsFacet.length > 0 &&
      allDocumentsFacet[0].data &&
      allDocumentsFacet[0].data.length > 0
    ) {
      const count = allDocumentsFacet[0].data[0].count;
      return count.toLocaleString('en-US', {
        maximumFractionDigits: 0,
      });
    }
  }

  const toggleFilters = () => {
    setFiltersIsOpen(!filtersIsOpen);
  };

  const closeFilters = () => {
    setFiltersIsOpen(false);

    if (openFiltersButtonRef.current) {
      openFiltersButtonRef.current.focus();
    }
  };

  const handleCloseMouseEnter = () => {
    setIsCloseHovered(true);
  };

  const handleCloseMouseLeave = () => {
    setIsCloseHovered(false);
  };

  const handleCloseFocus = () => {
    setIsCloseFocused(true);
  };

  const handleCloseBlur = () => {
    setIsCloseFocused(false);
  };

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape' && filtersIsOpen) {
        closeFilters();
      }
    };

    if (filtersIsOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [filtersIsOpen]);

  return (
    <>
      <Button
        StartIcon={SlidersIcon}
        aria-controls="filters-dialog"
        aria-expanded={filtersIsOpen}
        className={filtersButton}
        innerRef={openFiltersButtonRef}
        onClick={toggleFilters}
        variant="secondary"
      >
        Filters
      </Button>

      {filtersIsOpen && (
        <Overlay
          aria-hidden={filtersIsOpen ? 'false' : 'true'}
          onClick={closeFilters}
          role="presentation"
        />
      )}

      <FocusTrap
        active={filtersIsOpen}
        focusTrapOptions={{
          initialFocus: () => focusCaptureElementRef.current,
          onDeactivate: closeFilters,
          clickOutsideDeactivates: true,
        }}
      >
        <FiltersDialog
          aria-labelledby="filters-title"
          aria-modal="true"
          id="filters-dialog"
          isOpen={filtersIsOpen}
          role="dialog"
          tabIndex={filtersIsOpen ? 1 : -1}
        >
          {/* Hidden dummy element to capture initial focus */}
          <div
            ref={focusCaptureElementRef}
            style={{ position: 'absolute', top: '-10000px' }}
            tabIndex="-1"
          />

          <div className={filtersDialogHeader}>
            <h2 className={filtersDialogHeaderTitle} id="filters-title">
              Filters
            </h2>

            <button
              className={filtersDialogHeaderCloseButton}
              onBlur={handleCloseBlur}
              onClick={closeFilters}
              onFocus={handleCloseFocus}
              onMouseEnter={handleCloseMouseEnter}
              onMouseLeave={handleCloseMouseLeave}
              onTouchEnd={e => {
                e.preventDefault();
                closeFilters();
              }}
            >
              {isCloseHovered || isCloseFocused ? (
                <CloseCircleFillIcon />
              ) : (
                <CloseCircleIcon />
              )}
            </button>
          </div>

          <div className={filtersDialogContent}>{SearchFilters}</div>

          <div className={filtersDialogFooter}>
            <a href="#" onClick={() => clearFilters([LAYOUT_FILTER_FIELD])}>
              Clear all filters
            </a>

            <Button className={filtersShowResultsButton} onClick={closeFilters}>
              Show {getDocumentCount()} results
            </Button>
          </div>
        </FiltersDialog>
      </FocusTrap>
    </>
  );
}
